import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Avatar from '../components/Avatar';
import RideCard from '../components/RideCard';

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

function StatCard({ value, label, color = 'var(--accent)' }) {
  return (
    <div style={{
      background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
      borderRadius: 'var(--radius)', padding: '16px 12px', textAlign: 'center', flex: 1,
    }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { currentUser, rides, getMyConversations } = useApp();

  const upcomingRides = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return rides
      .filter(r => {
        const isDriver = r.driverId === currentUser.id;
        const isAcceptedRider = r.requests.some(req => req.riderId === currentUser.id && req.status === 'accepted');
        if (!isDriver && !isAcceptedRider) return false;
        const [y, m, d] = r.date.split('-').map(Number);
        const rDate = new Date(y, m - 1, d);
        return rDate >= today && r.status !== 'cancelled';
      })
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
      .slice(0, 3);
  }, [rides, currentUser]);

  const recentRides = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return rides
      .filter(r => {
        const [y, m, d] = r.date.split('-').map(Number);
        return new Date(y, m - 1, d) >= today && r.status === 'open';
      })
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 3);
  }, [rides]);

  const pendingRequests = useMemo(() => {
    return rides.filter(r =>
      r.driverId === currentUser.id &&
      r.requests.some(req => req.status === 'pending')
    );
  }, [rides, currentUser]);

  const convCount = getMyConversations().length;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, paddingTop: 8 }}>
        <div>
          <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 3 }}>{greeting()},</p>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>{currentUser.name.split(' ')[0]} 👋</h1>
        </div>
        <button onClick={() => navigate(`/profile/${currentUser.id}`)} style={{ background: 'none', padding: 0 }}>
          <Avatar user={currentUser} size={46} />
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <StatCard value={currentUser.completedRides} label="RIDES DONE" color="var(--accent)" />
        <StatCard
          value={currentUser.rating > 0 ? currentUser.rating.toFixed(1) : '—'}
          label="STAR RATING" color="var(--yellow)"
        />
        <StatCard value={convCount} label="CHATS" color="var(--green)" />
      </div>

      {/* Pending requests alert */}
      {pendingRequests.length > 0 && (
        <button
          onClick={() => navigate('/my-rides')}
          style={{
            width: '100%', background: 'var(--accent-light)',
            border: '1px solid rgba(59,130,246,0.4)',
            borderRadius: 'var(--radius-sm)', padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 20, cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)',
            }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>
              {pendingRequests.reduce((acc, r) => acc + r.requests.filter(req => req.status === 'pending').length, 0)} pending ride request{pendingRequests.reduce((acc, r) => acc + r.requests.filter(req => req.status === 'pending').length, 0) !== 1 ? 's' : ''}
            </span>
          </div>
          <ArrowRight />
        </button>
      )}

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
        <button
          onClick={() => navigate('/browse')}
          style={{
            background: 'linear-gradient(135deg, var(--navy-mid), var(--navy-light))',
            border: '1px solid var(--navy-border)',
            borderRadius: 'var(--radius)', padding: '18px 16px',
            textAlign: 'left', cursor: 'pointer',
          }}
        >
          <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>Find a Ride</div>
          <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Browse available rides</div>
        </button>
        {currentUser.isDriver && (
          <button
            onClick={() => navigate('/post-ride')}
            style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))',
              border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: 'var(--radius)', padding: '18px 16px',
              textAlign: 'left', cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 8 }}>🚗</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>Post a Ride</div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Offer seats to others</div>
          </button>
        )}
        {!currentUser.isDriver && (
          <button
            onClick={() => navigate('/edit-profile')}
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(16,185,129,0.04))',
              border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 'var(--radius)', padding: '18px 16px',
              textAlign: 'left', cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 8 }}>✏️</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>Become a Driver</div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Add your car details</div>
          </button>
        )}
      </div>

      {/* Upcoming rides */}
      {upcomingRides.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 className="section-title" style={{ margin: 0 }}>My Upcoming Rides</h2>
            <button onClick={() => navigate('/my-rides')} style={{
              background: 'none', color: 'var(--accent)', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              See all <ArrowRight />
            </button>
          </div>
          {upcomingRides.map(r => <RideCard key={r.id} ride={r} />)}
        </div>
      )}

      {/* Available rides */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 className="section-title" style={{ margin: 0 }}>Available Rides</h2>
          <button onClick={() => navigate('/browse')} style={{
            background: 'none', color: 'var(--accent)', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            Browse all <ArrowRight />
          </button>
        </div>
        {recentRides.length === 0 ? (
          <div style={{
            background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
            borderRadius: 'var(--radius)', padding: 24, textAlign: 'center',
          }}>
            <p style={{ color: 'var(--gray-400)', fontSize: 14 }}>No rides posted yet.</p>
            {currentUser.isDriver && (
              <button className="btn btn-primary" onClick={() => navigate('/post-ride')} style={{ marginTop: 12 }}>
                Post the first one!
              </button>
            )}
          </div>
        ) : (
          recentRides.map(r => <RideCard key={r.id} ride={r} />)
        )}
      </div>
    </div>
  );
}
